import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { post, RequestError } from "../api/client";
import type { LoginResponse, Permission, Principal, SessionResponse } from "../api/types";

const TOKEN_KEY = "lmcc-admin-token";

export type SessionState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authenticated"; token: string; principal: Principal; expiresAt: string };

interface SessionContextValue {
  state: SessionState;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

function readStoredToken(): string | undefined {
  try {
    return sessionStorage.getItem(TOKEN_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function storeToken(token: string | undefined): void {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage may be unavailable; the session then lasts for this page only.
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: "loading" });

  useEffect(() => {
    const token = readStoredToken();
    if (!token) {
      setState({ status: "anonymous" });
      return;
    }
    let cancelled = false;
    post<SessionResponse>("/auth/session", {}, token).then((session) => {
      if (cancelled) return;
      setState({ status: "authenticated", token, principal: session.principal, expiresAt: session.expiresAt });
    }).catch((error: unknown) => {
      if (cancelled) return;
      if (error instanceof RequestError && (error.status === 401 || error.status === 403)) storeToken(undefined);
      setState({ status: "anonymous" });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const session = await post<LoginResponse>("/auth/login", { username, password });
    storeToken(session.sessionToken);
    setState({
      status: "authenticated",
      token: session.sessionToken,
      principal: session.principal,
      expiresAt: session.expiresAt
    });
  }, []);

  const logout = useCallback(async () => {
    const token = state.status === "authenticated" ? state.token : undefined;
    storeToken(undefined);
    setState({ status: "anonymous" });
    if (token) {
      try {
        await post("/auth/logout", {}, token);
      } catch {
        // The token is already discarded locally; nothing else to do.
      }
    }
  }, [state]);

  const value = useMemo(() => ({ state, login, logout }), [state, login, logout]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession must be used inside SessionProvider");
  return context;
}

/** For components rendered under RequireSession, where the session is guaranteed. */
export function useAuthenticated(): { token: string; principal: Principal; expiresAt: string } {
  const { state } = useSession();
  if (state.status !== "authenticated") throw new Error("Not authenticated");
  return state;
}

export function usePermission(permission: Permission): boolean {
  const { state } = useSession();
  return state.status === "authenticated" && state.principal.permissions.includes(permission);
}
